import { flatten } from 'lodash';
import { useMemo } from 'react';

import {
  ById,
  LocalPromptDependency,
  PromptDependency,
  PromptInput,
  PromptOutput,
  PromptOutputProperty,
  PromptSingleTestCaseResult,
} from '../services/backendService/types';
import { notUndefined } from '../utils/general';

export const useVisiblePromptTestCaseInputs = ({
  availablePromptDependencies,
  promptDependencies,
  promptInputs,
}: {
  promptDependencies: PromptDependency[];
  availablePromptDependencies: LocalPromptDependency[];
  promptInputs?: PromptInput[];
}) => {
  const visibleInputs = useMemo(() => {
    return promptDependencies
      .map(dependency => {
        const foundDependency = availablePromptDependencies.find(
          dep => dep.id === dependency.dependencyId,
        );
        const foundInput = promptInputs?.find(
          input => input.id === dependency.dependencyId,
        );
        if (foundDependency && foundInput && foundInput.defaultValue) {
          return {
            ...foundDependency,
            defaultValue: foundInput.defaultValue,
          };
        }
        return foundDependency;
      })
      .filter(notUndefined);
  }, [availablePromptDependencies, promptDependencies, promptInputs]);

  return {
    visibleInputs,
  };
};

export const getAvailablePromptDependencies = ({
  promptInputs,
  promptOutputs,
  promptId,
}: {
  promptInputs?: PromptInput[];
  promptOutputs: (PromptOutput & {
    promptName: string;
    promptId: string;
  })[];
  promptId: string;
}) => {
  const inputs: LocalPromptDependency[] =
    promptInputs?.flatMap(input => {
      if (input.type === 'passage')
        return [
          {
            id: `${input.id}.title`,
            type: 'input',
            label: `${input.label}.title`,
            formType: 'passage',
            constraints: input.constraints,
          } as LocalPromptDependency,
          {
            id: `${input.id}.content`,
            type: 'input',
            label: `${input.label}.content`,
            formType: 'passage',
            constraints: input.constraints,
          } as LocalPromptDependency,
        ];
      return {
        id: input.id,
        type: 'input',
        label: input.label,
        formType: input.type,
        constraints: input.constraints,
      };
    }) || [];

  const outputs: LocalPromptDependency[] = promptOutputs.flatMap(output => {
    if (promptId === output.promptId) return []; // dont show outputs from your own prompt
    if (output.type === 'string')
      return [
        {
          id: output.promptId,
          type: 'output',
          label: `${output.promptName}: Text`,
          formType: 'shortText',
        } as LocalPromptDependency,
      ];

    if (output.type === 'array') return []; // not implemented

    if (output.type === 'object') {
      // Order the properties in the promptOutput according to the propertiesOrder
      const orderedPromptOutputProperties: {
        [name: string]: PromptOutputProperty;
      } =
        output.propertiesOrder && output.propertiesOrder.length > 0
          ? output.propertiesOrder.reduce(
              (acc, key) => {
                if (output.properties[key]) {
                  acc[key] = output.properties[key];
                }
                return acc;
              },
              {} as {
                [name: string]: PromptOutputProperty;
              },
            )
          : output.properties;

      const outputFields = Object.entries(orderedPromptOutputProperties).map(
        ([_, property]) => {
          if (property.type === 'array') {
            if (!property.items) return [];

            const fields = new Array(property.items.maxLength).fill(0).flatMap((_, i) => {
              if (!property.items) return undefined;
              if (!property.items.properties) {
                return [
                  {
                    id: `${output.promptId}.${property.id}.${i}`,
                    type: 'output',
                    label: `${output.promptName}: ${property.description}.${
                      property.items.type === 'number' ? 'Number' : 'Text'
                    }.${i}`,
                    formType: 'shortText',
                  } as LocalPromptDependency,
                ];
              }
              return Object.values(property.items.properties).map(itemProperty => {
                return {
                  id: `${output.promptId}.${property.id}.${i}.${itemProperty.id}`,
                  type: 'output',
                  label: `${output.promptName}: ${property.description}.${itemProperty.description}.${i}`,
                  formType: 'shortText',
                } as LocalPromptDependency;
              });
            });

            return fields.filter(notUndefined);
          }
          if (property.type === 'string' || property.type === 'number') {
            return [
              {
                id: `${output.promptId}.${property.id}`,
                type: 'output',
                label: `${output.promptName}: ${property.description}`,
                formType: 'shortText',
              } as LocalPromptDependency,
            ];
          }
          return []; // should never get here
        },
      );
      return flatten(outputFields.filter(notUndefined));
    }

    return []; // Should never get here
  });

  return [...(inputs || []), ...(outputs || [])];
};

export const useAvailablePromptDependencies = ({
  promptInputs,
  promptOutputs,
  promptId,
}: {
  promptInputs?: PromptInput[];
  promptOutputs: (PromptOutput & {
    promptName: string;
    promptId: string;
  })[];
  promptId: string;
}) => {
  const availablePromptDependencies = useMemo(() => {
    return getAvailablePromptDependencies({
      promptId,
      promptOutputs,
      promptInputs,
    });
  }, [promptInputs, promptOutputs, promptId]);

  return {
    availablePromptDependencies,
  };
};

export const useVisiblePromptDependentInputValues = ({
  visibleInputs,
  promptDependentInputs,
}: {
  visibleInputs: LocalPromptDependency[];
  promptDependentInputs?: PromptSingleTestCaseResult;
}) => {
  const visiblePromptDependentInputValues = useMemo(() => {
    const newTestCaseData = visibleInputs.reduce<PromptSingleTestCaseResult>(
      (acc, dependency) => {
        const dependencyId = dependency?.id;
        if (!dependencyId || !promptDependentInputs) return acc;

        const foundInput = promptDependentInputs[dependencyId];
        if (!foundInput) {
          return acc;
        }
        return {
          ...acc,
          [dependencyId]: foundInput,
        };
      },
      {},
    );
    return newTestCaseData;
  }, [promptDependentInputs, visibleInputs]);

  return {
    visiblePromptDependentInputValues,
  };
};
